import React from "react";

import { cn } from "../lib/utils";

/**
 * One column definition. Rendered as a table column at `md:` and up,
 * and as a stacked label/value pair inside a card on mobile.
 *
 * `key` identifies the column for React's reconciler — must be unique
 * within a table. `header` is the column heading (also doubles as the
 * label on mobile unless `mobileLabel` overrides). `cell` returns the
 * rendered content for a row. `className` lets callers add per-column
 * Tailwind utilities (e.g., `text-right` for numeric columns).
 *
 * `hideOnMobile` — when true, column is omitted from the mobile card.
 * Useful for low-priority columns operators don't need on a phone.
 */
export interface ResponsiveColumn<T> {
  key: string;
  header: string;
  mobileLabel?: string;
  className?: string;
  hideOnMobile?: boolean;
  cell: (row: T) => React.ReactNode;
}

interface ResponsiveDataTableProps<T> {
  columns: ResponsiveColumn<T>[];
  rows: T[];
  getRowKey: (row: T) => string;
  /** Optional header for the mobile card (e.g., the SCU). Receives the
   *  full row so callers can compose multi-piece headers. Falls back to
   *  the first column's `cell` when absent. */
  mobileHeader?: (row: T) => React.ReactNode;
  /** Optional click handler — applied to both `<tr>` and the mobile
   *  card. Adds `cursor-pointer` styling automatically. */
  onRowClick?: (row: T) => void;
  /** Empty-state element rendered when `rows.length === 0`. */
  emptyState?: React.ReactNode;
}

/**
 * Renders a wide data table at `md:` and up; a stack of cards below.
 *
 * Mobile card layout:
 *   - Optional header (mobileHeader prop) on the first line
 *   - Each non-hidden column as a "Label: value" row in a 2-col grid
 *
 * The component is intentionally tiny — no sorting, no pagination, no
 * sticky headers. Pages compose their own controls (filters / chips /
 * sorts) outside this component and pass the already-filtered rows in.
 */
export function ResponsiveDataTable<T>({
  columns,
  rows,
  getRowKey,
  mobileHeader,
  onRowClick,
  emptyState,
}: ResponsiveDataTableProps<T>): React.ReactElement {
  if (rows.length === 0 && emptyState) {
    return <>{emptyState}</>;
  }

  const visibleMobileColumns = columns.filter((c) => !c.hideOnMobile);

  return (
    <>
      {/* Desktop / tablet table — md:+ */}
      <div className="hidden md:block overflow-auto rounded-md border border-secondary-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-secondary-100 text-left text-xs uppercase tracking-wide text-secondary-700">
            <tr>
              {columns.map((c) => (
                <th key={c.key} className={cn("px-3 py-2", c.className)}>
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr
                key={getRowKey(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={cn(
                  i % 2 === 0 ? "bg-white" : "bg-secondary-50",
                  onRowClick && "cursor-pointer hover:bg-primary-50",
                )}
              >
                {columns.map((c) => (
                  <td key={c.key} className={cn("px-3 py-2", c.className)}>
                    {c.cell(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile card layout — under md: */}
      <div className="md:hidden flex flex-col gap-2">
        {rows.map((row) => (
          <div
            key={getRowKey(row)}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            className={cn(
              "rounded-md border border-secondary-200 bg-white p-3",
              onRowClick && "cursor-pointer active:bg-secondary-50",
            )}
          >
            {mobileHeader && (
              <div className="mb-2 pb-2 border-b border-secondary-100">
                {mobileHeader(row)}
              </div>
            )}
            <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1.5 text-sm">
              {visibleMobileColumns.map((c) => (
                <React.Fragment key={c.key}>
                  <dt className="text-secondary-500 text-xs uppercase tracking-wide self-center">
                    {c.mobileLabel ?? c.header}
                  </dt>
                  <dd className="text-secondary-900">{c.cell(row)}</dd>
                </React.Fragment>
              ))}
            </dl>
          </div>
        ))}
      </div>
    </>
  );
}

export default ResponsiveDataTable;
